import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import {
  Work as WorkIcon,
  School as SchoolIcon,
  MilitaryTech as MilitaryIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import type { Person, Location } from '../../../../types';

interface ProfessionalPanelProps {
  person: Person;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * ProfessionalPanel - Displays professional information
 *
 * Shows:
 * - Occupation(s)
 * - Education
 * - Military service
 * - Career timeline
 */
export const ProfessionalPanel: React.FC<ProfessionalPanelProps> = ({
  person,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const formatLocation = (location?: Location): string => {
    if (!location) return '';
    const parts = [location.city, location.state, location.country].filter(Boolean);
    return parts.join(', ');
  };

  const hasOccupation = person.occupation || (person.occupations && person.occupations.length > 0);
  const hasEducation = person.education;
  const hasMilitary = person.militaryService;
  const hasAnyProfessionalInfo = hasOccupation || hasEducation || hasMilitary;

  if (!hasAnyProfessionalInfo) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No professional information available
      </Typography>
    );
  }

  return (
    <Box>
      <List dense>
        {/* Primary Occupation */}
        {person.occupation && (
          <ListItem>
            <ListItemIcon>
              <WorkIcon />
            </ListItemIcon>
            <ListItemText
              primary="Occupation"
              secondary={person.occupation}
            />
          </ListItem>
        )}

        {/* Multiple Occupations */}
        {person.occupations && person.occupations.length > 0 && (
          <ListItem>
            <ListItemIcon>
              <WorkIcon />
            </ListItemIcon>
            <ListItemText
              primary="Occupations"
              secondary={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {person.occupations.map((occ, index) => (
                    <Chip
                      key={index}
                      label={occ}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              }
            />
          </ListItem>
        )}

        {/* Education */}
        {person.education && (
          <ListItem>
            <ListItemIcon>
              <SchoolIcon />
            </ListItemIcon>
            <ListItemText
              primary="Education"
              secondary={person.education}
            />
          </ListItem>
        )}

        {/* Military Service */}
        {person.militaryService && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem>
              <ListItemIcon>
                <MilitaryIcon />
              </ListItemIcon>
              <ListItemText
                primary="Military Service"
                secondary={person.militaryService.branch}
              />
            </ListItem>

            {person.militaryService.rank && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Rank"
                  secondary={person.militaryService.rank}
                />
              </ListItem>
            )}

            {person.militaryService.serviceNumber && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Service Number"
                  secondary={person.militaryService.serviceNumber}
                />
              </ListItem>
            )}

            {(person.militaryService.startDate || person.militaryService.endDate) && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Service Period"
                  secondary={`${person.militaryService.startDate || '?'} - ${person.militaryService.endDate || 'Present'}`}
                />
              </ListItem>
            )}

            {person.militaryService.unit && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Unit"
                  secondary={person.militaryService.unit}
                />
              </ListItem>
            )}

            {person.militaryService.conflicts && person.militaryService.conflicts.length > 0 && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Conflicts"
                  secondary={person.militaryService.conflicts.join(', ')}
                />
              </ListItem>
            )}

            {person.militaryService.decorations && person.militaryService.decorations.length > 0 && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemText
                  primary="Decorations"
                  secondary={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {person.militaryService.decorations.map((dec, index) => (
                        <Chip
                          key={index}
                          label={dec}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItem>
            )}

            {person.militaryService.stationed && person.militaryService.stationed.length > 0 && (
              <ListItem sx={{ pl: 7 }}>
                <ListItemIcon>
                  <LocationIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Stationed"
                  secondary={person.militaryService.stationed.map(loc => formatLocation(loc)).filter(Boolean).join('; ')}
                />
              </ListItem>
            )}
          </>
        )}
      </List>
    </Box>
  );
};
